import React, { useState } from 'react'

import { isScreenSizeSmall } from '../common/utils'

/**
 * Props for the ExpandableRow component.
 *
 * @property {string} rowIndex - The index of the row to toggle.
 * @property {(isExpanded: () => boolean, toggleExpansion: () => void) => React.ReactNode} children -
 * A function that receives the expansion state and toggle function, and returns the row content.
 */
type ExpandableRowProps = {
  rowIndex: string
  children: (
    isExpanded: () => boolean,
    toggleExpansion: () => void,
  ) => React.ReactNode
}

/**
 * ExpandableRow component.
 *
 * This component manages the expansion state of a row in a table.
 * It provides a function to toggle the expansion state and a function to check if the row is expanded.
 *
 * @param {ExpandableRowProps} props - The props for the component.
 *
 * Usage:
 * - Wrap the row content with the ExpandableRow component.
 * - The children function receives the isExpanded and toggleExpansion functions to manage the row's expansion state.
 */
const ExpandableRow: React.FC<ExpandableRowProps> = ({
  rowIndex,
  children,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  /**
   * Toggles the expansion state of the row.
   */
  const toggleRowExpansion = (): void => {
    if (!isScreenSizeSmall()) return

    const updatedExpandedRows = new Set(expandedRows)
    if (updatedExpandedRows.has(rowIndex)) {
      updatedExpandedRows.delete(rowIndex)
    } else {
      updatedExpandedRows.add(rowIndex)
    }
    setExpandedRows(updatedExpandedRows)
  }

  /**
   * Checks if the row is expanded.
   *
   * @returns {boolean} - True if the row is expanded, false otherwise.
   */
  const isRowExpanded = (): boolean => {
    if (!isScreenSizeSmall()) {
      return true
    }
    return expandedRows.has(rowIndex)
  }

  return <>{children(isRowExpanded, toggleRowExpansion)}</>
}

export default ExpandableRow
